import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, Cpu, Wifi, WifiOff } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Card, Btn } from "@/components/ui-kit";
import { useRealtime } from "@/hooks/useRealtime";

export function PrintAgentCard() {
  const { restaurant } = useAuth();
  const { t } = useT();
  const rid = restaurant.id;
  const [st, setSt] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/restaurants/${rid}/print-agent`).then((r) => setSt(r.data)); api.get(`/restaurants/${rid}/print-jobs`, { params: { limit: 8 } }).then((r) => setJobs(r.data)); }, [rid]);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  useRealtime(rid, load, ["print_jobs"]);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/restaurants/${rid}/print-agent/download`, { params: { origin: window.location.origin }, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "menuone_print_agent.py"; a.click(); URL.revokeObjectURL(url); load();
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  const regen = async () => { if (!window.confirm(t("agent_regen_confirm"))) return; await api.post(`/restaurants/${rid}/print-agent/token`); toast.success(t("agent_token_regen")); load(); };
  const hm = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const jobColor = { done: "text-emerald-700", failed: "text-rose-600", pending: "text-slate-500", sent: "text-amber-600" };
  return (
    <Card className="lg:col-span-2" data-testid="print-agent-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-heading font-bold"><Cpu size={18} className="text-[#4a8a56]" />{t("print_agent")}</p>
          <p className="mt-1 text-sm text-slate-600">{t("print_agent_desc")}</p>
        </div>
        {st && <span data-testid="agent-status" className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${st.online ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{st.online ? <Wifi size={12} /> : <WifiOff size={12} />}{st.online ? `${t("agent_online")} · ${st.host || ""}` : st.configured ? t("agent_offline") : t("agent_not_installed")}</span>}
      </div>
      <ol className="mt-3 list-decimal space-y-1 pl-5 text-xs text-slate-600">
        <li>{t("agent_step1")}</li><li>{t("agent_step2")}</li><li>{t("agent_step3")}</li>
      </ol>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Btn data-testid="agent-download-btn" disabled={busy} onClick={download}><Download size={16} />{t("download_agent")}</Btn>
        {st?.configured && <Btn data-testid="agent-regen-btn" variant="ghost" className="text-xs" onClick={regen}><RefreshCw size={14} />{t("agent_regen")}</Btn>}
        {st?.last_seen && <span className="text-xs text-slate-400">{t("last_contact")}: {new Date(st.last_seen).toLocaleString("it-IT")}</span>}
      </div>
      {jobs.length > 0 && <div className="mt-4 border-t border-slate-100 pt-3" data-testid="print-jobs">
        <p className="mb-1 text-xs font-semibold uppercase text-slate-500">{t("recent_jobs")}</p>
        <ul className="space-y-0.5 text-xs">{jobs.map((j) => <li key={j.id} data-testid={`print-job-${j.id}`} className="flex items-center gap-2"><span className="font-mono text-slate-400">{hm(j.created_at)}</span><span>{j.kind === "probe" ? t("test_connection") : j.kind === "ticket" ? `${t("job_ticket")} #${j.order_number || ""}` : j.kind === "prebill" ? `${t("job_prebill")} #${j.order_number || ""}` : t("job_receipt")} → {j.printer_name}</span><span className={`font-bold ${jobColor[j.status]}`}>{t(`job_${j.status}`)}</span>{j.result && <span className="truncate text-slate-400">{j.result}</span>}</li>)}</ul>
      </div>}
    </Card>
  );
}
